import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { TravelItineraryEnum } from "@b2bportal/air-booking-api";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { CheckoutPage, ConfirmationPageHeader } from "@components/checkout";
import {
  ContactSelectors,
  FlightSelectors,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useGetAccountsRewardsQuery } from "@hsbc/rewards";
import { Icon } from "@tenant/ui";
import { ConnectedTripDetails } from "../../connectedComponents";
import styles from "./styles.module.scss";

export const ConfirmationPage = () => {
  const [, cn] = useModuleBEM(styles, "ConfirmationPage");
  const { t } = useI18nContext();
  const navigate = useNavigate();

  const contactInformation = useSelector(
    ContactSelectors.getContactInformation
  );
  const itinerary = useSelector(FlightSelectors.getFinalizedItinerary);

  useEffect(() => {
    document.title = t("bookingConfirmation.documentTitle");
    return () => {
      document.title = t("documentTitle");
    };
  }, [t]);

  const refreshPoints = useGetAccountsRewardsQuery(undefined).refetch;

  useEffect(() => {
    // Refresh points balance on after booking charge
    refreshPoints();
  }, [refreshPoints]);

  const locator = useMemo(() => {
    const getBookingLocator = () => {
      switch (itinerary?.TravelItinerary) {
        case TravelItineraryEnum.SingleTravelItinerary: {
          return `${itinerary.locators.b2b}`;
        }
        case TravelItineraryEnum.MultiTravelItinerary: {
          // Concatenates locators when multiple trips are booked.
          return itinerary.travelItineraries
            ?.map(
              (itineraries, index) =>
                `${index !== 0 ? "#" : ""}${itineraries.locators.b2b}`
            )
            ?.join(", ");
        }
        default:
          return "";
      }
    };

    return t("core-flights.checkout.bookingConfirmation.confirmationId", {
      id: getBookingLocator(),
    });
  }, [itinerary, t]);

  return (
    <CheckoutPage
      onContinue={() => navigate("/")}
      header={<ConfirmationPageHeader />}
    >
      <div className={cn("container")}>
        <div className={cn("content")}>
          <div className={cn("title")}>
            <Icon iconName={IconNameEnum.check} />
            <h2>{t("core-flights.checkout.bookingConfirmation.title")}</h2>
          </div>
          <div className={cn("subtitle")}>
            {t("core-flights.checkout.bookingConfirmation.subtitle", {
              email: contactInformation.email,
            })}
          </div>
          <div className={cn("confirmation-id")}>{locator}</div>
          <div className={cn("border")}></div>
          <ConnectedTripDetails showActionLinks={false} />
        </div>
      </div>
    </CheckoutPage>
  );
};
